import { Injectable } from '@angular/core';
import Shepherd from 'shepherd.js';
import { Router } from '@angular/router';
import { NgZone } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class TourService {
  private tour: any;
  public readonly MENUBAR_TOUR_KEY = 'menubar_tour_shown';
  public readonly IMPULSES_TOUR_KEY = 'impulses_tour_shown';
  public readonly ORDERS_TOUR_KEY = 'orders_tour_shown';
  public readonly VIEW_PACKAGE_TOUR_KEY = 'view_package_tour_shown';
  public readonly ASSIGNED_PART_TOUR_KEY = 'assigned_part_tour_shown';
  public readonly CREATE_PACKAGE_TOUR_KEY = 'create_package_tour_shown';
  public readonly FYC_TOUR_KEY = 'fyc_tour_shown';
  private pathTriggerSubject = new Subject<string>();
  pathTrigger$ = this.pathTriggerSubject.asObservable();
  private tooltipTextSubject = new BehaviorSubject<string>('Start Guided Tour');
  tooltipText$ = this.tooltipTextSubject.asObservable();
  private activeTour: Shepherd.Tour | null = null;
  setTooltipText(text: string) {
    this.tooltipTextSubject.next(text);
  }
  triggerTourPath(path: string) {
    this.pathTriggerSubject.next(path);
  }
  constructor(private router: Router, private zone: NgZone) {
    this.tour = new Shepherd.Tour({
      defaultStepOptions: {
        cancelIcon: { enabled: true },
        scrollTo: { behavior: 'smooth', block: 'center' },
        classes: 'shepherd-theme-arrows',
      },
      useModalOverlay: true
    });
    this.tour.on('cancel', () => {
      localStorage.setItem(this.MENUBAR_TOUR_KEY, 'true');
    });
  }
  private waitForMenuItems(texts: string[], timeout = 5000): Promise<void> {
    return new Promise((resolve, reject) => {
      const start = Date.now();

      const check = () => {
        const foundAll = texts.every(text => {
          const link = Array.from(document.querySelectorAll('a.p-menuitem-link')).find(link => {
            const span = link.querySelector('span.p-menuitem-text');
            return span?.textContent?.trim() === text;
          });
          return !!link;
        });

        if (foundAll) return resolve();
        if (Date.now() - start > timeout) return reject('Menu items not found in time');
        requestAnimationFrame(check);
      };

      check();
    });
  }


  private findMenuItemByText(text: string): HTMLElement | null {
    const links = Array.from(document.querySelectorAll('a.p-menuitem-link'));

    for (const link of links) {
      const span = link.querySelector('span.p-menuitem-text');
      if (span?.textContent?.trim() === text) {
        return link as HTMLElement;
      }
    }
    return null;
  }
waitForViewLink(timeoutMs = 5000): Promise<void> {
  return new Promise((resolve, reject) => {
    const start = Date.now();
    const checkExist = () => {
      const el = document.querySelector('tr:first-child td a[href*="/details"]');
      if (el) resolve();
      else if (Date.now() - start > timeoutMs) reject(new Error('Timeout waiting for view link'));
      else setTimeout(checkExist, 200);
    };
    checkExist();
  });
}

waitForToolbarButtons(timeoutMs = 5000): Promise<void> {
  const selectors = [
    '#btnAddPart',
    '#btnOrderDocument',
    '#btnPartPriceHistory',
    '#btnOAS',
    '#btnExportExcel',
    '#btnSearchByDropdown'
  ];

  return new Promise((resolve, reject) => {
    const start = Date.now();
    const checkExist = () => {
      const allPresent = selectors.every(sel => document.querySelector(sel));
      if (allPresent) resolve();
      else if (Date.now() - start > timeoutMs) reject(new Error('Timeout waiting for toolbar buttons'));
      else setTimeout(checkExist, 200);
    };
    checkExist();
  });
}

startMenubarTour() {

    const skipMenuTour = sessionStorage.getItem('skipMenuTour') === 'true';
    const alreadyShown = localStorage.getItem(this.MENUBAR_TOUR_KEY) === 'true';
     const resumeFromFYC = sessionStorage.getItem('continueMenuFromFYC') === 'true';

    if (skipMenuTour || alreadyShown) {
      sessionStorage.removeItem('skipMenuTour');
      return;
    }


    this.waitForMenuItems([
      'Impulses',
      'Package Planning',
      'Orders',
      'Find Your Colleague',
      'Procurement Strategy Hub'
    ])
      .then(() => {
        console.log('[Tour] Menu items found. Adding tour steps.');
        if (resumeFromFYC) {
          console.log('Starting tour steps from FY.');
        sessionStorage.removeItem('continueMenuFromFYC');
        localStorage.setItem(this.MENUBAR_TOUR_KEY, 'false');

        this.tour.addStep({
          title: 'Find Your Colleague',
          text: 'Locate and connect with colleagues here.',
          attachTo: {
            element: 'a.p-menuitem-link[href="/find-your-colleague"]',
            on: 'bottom'
          },
          buttons: [
            {
              text: 'Next',
              action: this.tour.next,
              classes: 'btn-next'
            },
            {
              text: 'Explore FYC',
              action: () => {
                sessionStorage.setItem('continueFYCTour', 'true');
                this.tour.complete();
                this.zone.run(() => {
                  this.router.navigate(['/find-your-colleague'], {
                    queryParams: { continueTour: 'true' }
                  });
                });
              },
              classes: 'btn-next'
            }
          ]
        });

        this.tour.start();
        return;
      }
       

        this.tour.addStep({
          title: 'Impulses',
          text: 'This section shows impulse data. You can either explore it now or continue with the tour.',
          attachTo: {
            element: 'a.p-menuitem-link[href="/impulses"]',
            on: 'right'
          },
          buttons: [
            {
              text: 'Next',
              action: this.tour.next,
              classes: 'btn-next'
            },
            {
              text: 'Go to Impulses Page',
              action: () => {
                console.log('[Tour] Navigating to impulses with tour flag');
                sessionStorage.setItem('skipMenuTour', 'true');
                sessionStorage.setItem('continueImpulseTour', 'true');

                this.tour.complete();

                this.zone.run(() => {
                  this.router.navigate(['/impulses'], {
                    queryParams: { continueTour: 'true' }
                  });
                });
              },
              classes: 'btn-next'
            }
          ]
        });

        
    this.tour.addStep({
      title: 'Package Planning',
      text: 'Plan and manage part packages here.',
      attachTo: {
        element: this.findMenuItemByText('Package Planning'),
        on: 'bottom'
      },
      buttons: [
        { text: 'Next', action: this.tour.next, classes: 'btn-next' },
        {
          text: 'View Packages',
          action: () => {
            sessionStorage.setItem('skipMenuTour', 'true');
            sessionStorage.setItem('continueViewPackageTour', 'true');
            this.tour.complete();
            this.zone.run(() => {
              this.router.navigate(['/part-package/list'], {
                queryParams: { continueTour: 'true' }
              });
            });
          },
          classes: 'btn-next'
        },
        {
          text: 'Create Package',
          action: () => {
            sessionStorage.setItem('skipMenuTour', 'true');
            sessionStorage.setItem('continueCreatePackageTour', 'true');
            this.tour.complete();
            this.zone.run(() => {
              this.router.navigate(['/part-package/create-package'], {
                queryParams: { continueTour: 'true' }
              });
            });
          },
          classes: 'btn-next'
        }
      ]
    });


        this.tour.addStep({
          title: 'Find Your Colleague',
          text: 'Locate and connect with colleagues here.',
          attachTo: {
            element: 'a.p-menuitem-link[href="/find-your-colleague"]',
            on: 'bottom'
          },
          buttons: [
            {
              text: 'Next',
              action: this.tour.next,
              classes: 'btn-next'
            },
            {
              text: 'Explore FYC',
              action: () => {
                sessionStorage.setItem('continueFYCTour', 'true');
                this.tour.complete();

                this.zone.run(() => {
                  this.router.navigate(['/find-your-colleague'], {
                    queryParams: { continueTour: 'true' }
                  });
                });
              },
              classes: 'btn-next'
            }
          ]
        });

        this.tour.addStep({
          title: 'Orders',
          text: 'Check your order history and new orders here.',
          attachTo: {
            element: 'a.p-menuitem-link[href="/orders"]',
            on: 'right'
          },
          buttons: [
            {
              text: 'Next',
              action: this.tour.next,
              classes: 'btn-next'
            },
            {
              text: 'Go to Orders Page',
              action: () => {
                console.log('[Tour] Navigating to Orders with tour flag');
                sessionStorage.setItem('continueOrdersTour', 'true');
                this.tour.complete();

                this.zone.run(() => {
                  this.router.navigate(['/orders'], {
                    queryParams: { continueTour: 'true' }
                  });
                });
              },
              classes: 'btn-next'
            }
          ]
        });

        this.tour.addStep({
          title: 'Procurement Strategy Hub',
          text: 'Explore various sourcing strategies and teams.',
          attachTo: {
            element: this.findMenuItemByText('Procurement Strategy Hub'),
            on: 'bottom'
          },
          buttons: [{ text: 'Next', action: this.tour.next, classes: 'btn-next' }]
        });

        this.tour.addStep({
          title: 'User Menu',
          text: 'Click here to access your profile and feedback options.',
          attachTo: {
            element: '.menu-button',
            on: 'bottom',
          },
          buttons: [{
            text: 'Done', action: () => {
              this.tour.complete();
              localStorage.setItem(this.MENUBAR_TOUR_KEY, 'true');
            }, classes: 'btn-next'
          }],
        });

        this.tour.start();
      })
      .catch((err) => {
        console.warn('Tour could not start:', err);
      });
}

startImpulseTour(searchCallback?: () => void): void {
   if (this.activeTour) {
    console.warn('A tour is already running.');
    return;
  }
  const tour = new Shepherd.Tour({
    defaultStepOptions: {
      cancelIcon: { enabled: true },
      scrollTo: { behavior: 'smooth', block: 'center' },
      classes: 'shepherd-theme-arrows',
    },
    useModalOverlay: true
  });
   this.activeTour = tour;
   tour.addStep({
  id: 'intro',
  title: 'Impulses Filters',
  text: 'Use these fields to filter impulses.',
  modalOverlayOpeningPadding: 0,
  classes: 'shepherd-theme-arrows shepherd-centered',
  buttons: [
    {
      text: 'Next',
      action: tour.next,
      classes: 'btn-next'
    }
  ]
});

  const clearActive = () => this.activeTour = null;
  tour.on('cancel', () => {
    localStorage.setItem(this.MENUBAR_TOUR_KEY, 'true');
    localStorage.setItem(this.IMPULSES_TOUR_KEY, 'true');
    clearActive();
  });
  tour.on('complete', clearActive);

  const alreadyShown = localStorage.getItem(this.IMPULSES_TOUR_KEY) === 'true';
  const menubarTourShown = localStorage.getItem(this.MENUBAR_TOUR_KEY) === 'true';
  if (alreadyShown || menubarTourShown) return;

  tour.addStep({
    title: 'Search Fields',
    text: 'Enter a purchaser code. Purchaser code is mandatory when filtering with state or type.',
    attachTo: { element: '#purchaserCode', on: 'bottom' },
    buttons: [{
      text: 'Next',
      action: () => {
        const input = document.querySelector('#purchaserCode') as HTMLInputElement;
        if (input) input.value = 'B45';
        tour.next();
      },
      classes: 'btn-next'
    }]
  });

  tour.addStep({
    title: 'MR No.',
    text: 'Enter the material requisition number.',
    attachTo: { element: '#mrNo', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

   tour.addStep({
    title: 'Part description',
    text: 'Enter part description.',
    attachTo: { element: '#partDescription', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Scania Part No',
    text: 'Enter a part number.',
    attachTo: { element: '#partNo', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

 
  tour.addStep({
    title: 'Package ID',
    text: 'Enter a package ID.',
    attachTo: { element: '#packageId', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'State',
    text: 'Select one of the states from the drop-down menu.',
    attachTo: { element: '#state', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Type',
    text: 'Select one of the types from the drop-down menu.',
    attachTo: { element: '#type', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });
  tour.addStep({
    title: 'Search',
    text: 'Click here to search impulses based on the entered filters.',
    attachTo: { element: '#search', on: 'top' },
    buttons: [{
      text: 'Search',
      action: () => {
        searchCallback?.();
        setTimeout(() => {
          this.waitForToolbarButtons().then(() => {
            const toolbarSteps = [
              {
                title: 'Add Part To Package',
                text: 'Click to add selected parts to a package.',
                attachTo: { element: '#btnAddPart', on: 'bottom' as const }
              },
              {
                title: 'Order Document',
                text: 'Generate an order document.',
                attachTo: { element: '#btnOrderDocument', on: 'bottom' as const }
              },
              {
                title: 'Part Price History',
                text: 'View price history for the selected part.',
                attachTo: { element: '#btnPartPriceHistory', on: 'bottom' as const }
              },
              {
                title: 'OAS',
                text: 'Open OAS for the selected part.',
                attachTo: { element: '#btnOAS', on: 'bottom' as const }
              },
              {
                title: 'Export to Excel',
                text: 'Export search results to Excel.',
                attachTo: { element: '#btnExportExcel', on: 'bottom' as const }
              },
              {
                title: 'Search By Dropdown',
                text: 'Use this dropdown to perform specific searches.',
                attachTo: { element: '#btnSearchByDropdown', on: 'bottom' as const }
              }
            ];

            toolbarSteps.forEach(step => {
              tour.addStep({
                ...step,
                buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
              });
            });

            this.waitForViewLink().then(() => {
              tour.addStep({
                title: 'View Details',
                text: 'Click here to view the full details of an impulse.',
                attachTo: { element: 'tr:first-child td a[href*="/details"]', on: 'bottom' },
                buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{
                  text: 'Next',
                  classes: 'btn-next',
                  action: () => {
                    const viewLink = document.querySelector('.view-link-tour') as HTMLElement;
                    if (viewLink) {
                      localStorage.setItem('continueRequisitionTour', 'true');
                      viewLink.click();
                    } else {
                      console.warn('View link not found!');
                    }
                    tour.complete();
                  }
                }]
              });

              tour.next();
            });

          });
        }, 1500);
      },
      classes: 'btn-next'
    }]
  });

  tour.start();
}

startCreateOrderTour() {
  
  const tour = new Shepherd.Tour({
    defaultStepOptions: {
      scrollTo: true,
      cancelIcon: { enabled: true },
      canClickTarget: false
    }
  });
tour.addStep({
  id: 'orders-intro',
  title: 'Search & Filter Orders',
  text: 'Use these fields to create a new prototype order.',
  classes: 'shepherd-theme-arrows shepherd-centered',
  buttons: [
    {
      text: 'Next',
      action: tour.next,
      classes: 'btn-next'
    }
  ]
});

  tour.addStep({
    title: 'Order No',
    text: 'This is the order number. It is auto-generated and not editable.',
    attachTo: { element: '#order-no', on: 'bottom' },
    buttons: [{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Order Date',
    text: 'This is the date of when the order was created. It is auto-generated and not editable.',
    attachTo: { element: '#order-date', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Supplier No',
    text: 'This is the supplier number. It is auto-generated and not editable.',
    attachTo: { element: '#supplier-no', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Supplier Name',
    text: 'This is the supplier`s name. It is auto-generated and not editable.',
    attachTo: { element: '#suppliername', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'DUNS No',
    text: 'This is the supplier DUNS number. It works as a global business ID. It is auto-generated and not editable.',
    attachTo: { element: '#dunsno', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Delivery Date',
    text: 'Select the expected delivery date for the order.',
    attachTo: { element: '#delivery-date', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

   tour.addStep({
    title: 'User',
    text: 'Enter the name of the user creating the order.',
    attachTo: { element: '#user-id', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Purchaser Code',
    text: 'Select the purchaser code from the drop-down menu.',
    attachTo: { element: '#purchaser-code', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Currency',
    text: 'Select the currency to be used for this order from the drop-down menu.',
    attachTo: { element: '#currency', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Goods Address',
    text: 'Select the delivery address for the goods from the drop-down menu.',
    attachTo: { element: '#goods-address', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Payment Terms',
    text: 'Select the applicable payment terms from the drop-down menu.',
    attachTo: { element: '#payment-terms', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Delivery Terms',
    text: 'Select the delivery terms for the order from the drop-down menu.',
    attachTo: { element: '#delivery-terms', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Place',
    text: 'Enter the delivery location associated with the delivery terms.',
    attachTo: { element: '#delivery-place', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Shipment Method',
    text: 'Select the shipment method from the drop-down menu',
    attachTo: { element: '#shipment-method', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Packing',
    text: 'Choose how the goods should be packed from the drop-down menu.',
    attachTo: { element: '#packing', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Account',
    text: 'The account number is filled in automatically.',
    attachTo: { element: '#account', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Cost Centre',
    text: 'The cost center related to this order is filled in automatically.',
    attachTo: { element: '#cost-centre', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Project Number',
    text: 'The project number is filled in automatically.',
    attachTo: { element: '#project-number', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Spec Code',
    text: 'Enter the specification code if required.',
    attachTo: { element: '#spec-code', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Subgroup',
    text: 'Select a subgroup for classification.',
    attachTo: { element: '#subgroup', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Activity ID',
    text: 'Enter the activity ID associated with the order.',
    attachTo: { element: '#activity-id', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Contact Person',
    text: 'Enter the name of the contact person for this order.',
    attachTo: { element: '#contact-person', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Responsible Deliveries',
    text: 'Select the person responsible for deliveries.',
    attachTo: { element: '#responsible-deliveries', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Remarks',
    text: 'Enter any additional remarks related to the order.',
    attachTo: { element: '#remarks', on: 'bottom' },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Add /Delete Impulses',
    text: 'Use these buttons to manage impulses for the order.',
    attachTo: { element: '#impulses-buttons', on: 'bottom' }, 
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Save / Cancel',
    text: 'Click “Save” to submit the order or “Cancel” to discard it.',
    attachTo: { element: '#save-cancel-buttons', on: 'bottom' }, 
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Done', action: tour.complete, classes: 'btn-next' }]
  });

  tour.start();
}

startOrdersTour() {
    const tour = new Shepherd.Tour({
      defaultStepOptions: {
        scrollTo: true,
        cancelIcon: { enabled: true }
      }
    });

    tour.on('cancel', () => {
      localStorage.setItem(this.MENUBAR_TOUR_KEY, 'true');
      localStorage.setItem(this.ORDERS_TOUR_KEY, 'true');
    });

    tour.addStep({
  id: 'orders-intro',
  title: 'Search & Filter Orders',
  text: 'Use these fields to search and filter orders.',
  classes: 'shepherd-theme-arrows shepherd-centered',
  buttons: [
    {
      text: 'Next',
      action: tour.next,
      classes: 'btn-next'
    }
  ]
});

    tour.addStep({
      title: 'Order State',
      text: 'Select the order state.',
      attachTo: {
        element: '#sqa',
        on: 'bottom'
      },
      buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
    });

    tour.addStep({
      title: 'Order Number',
      text: 'Enter the order number to find specific orders',
      attachTo: {
        element: '#orderNumber',
        on: 'bottom'
      },
      buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
    });

    tour.addStep({
      title: 'Purchaser Code',
      text: 'Search by purchaser code',
      attachTo: {
        element: '#purchaserCode',
        on: 'bottom'
      },
      buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
    });

    tour.addStep({
      title: 'Order Type',
      text: 'Choose one of the order types from the drop-down menu.',
      attachTo: {
        element: '#orderType',
        on: 'bottom'
      },
      buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
    });

    tour.addStep({
      title: 'Order Placed By',
      text: 'Enter the user’s name to search for orders placed by that user',
      attachTo: {
        element: '#orderPlacedBy',
        on: 'bottom'
      },
      buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
    });

    tour.addStep({
      title: 'Part No.',
      text: 'Search by using the part number.',
      attachTo: {
        element: '#partNo',
        on: 'bottom'
      },
      buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
    });

    tour.addStep({
      title: 'Order Date Range',
      text: 'Choose a date range from the drop-down menu',
      attachTo: {
        element: '#dateRange',
        on: 'bottom'
      },
      buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
    });

     tour.addStep({
      title: 'Search Orders',
      text: 'Click this button to search orders based on the filters.',
      attachTo: {
        element: '#search',
        on: 'bottom'
      },
      buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{
      text: 'Search & View',
     action: () => {
        window.dispatchEvent(new Event('triggerSearchOrders'));
       const interval = setInterval(() => {
          const table = document.querySelector('neo-matris-table');
          if (table) {
            clearInterval(interval);
            tour.next();
          }
        }, 500);
      
      },
      classes: 'btn-next'
    },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
    });

    tour.addStep({
      title: 'Clear Filters',
      text: 'Click this button to clear previous search.',
      attachTo: {
        element: '#clear button',
        on: 'bottom'
      },
      buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
    });

    tour.addStep({
      title: 'Create New Order',
      text: `Click this button to create a new order. Choose the specific order type from the drop-down menu.`,
      attachTo: {
        element: '#create-order-btn button',
        on: 'bottom'
      },
      buttons: [
        {
          text: 'Explore Create Order Page',
          action: () => {
            sessionStorage.setItem('continueCreateOrderTour', 'true');
            tour.complete();

            this.zone.run(() => {
              this.router.navigate(['orders/create-new-order', { orderType: 'EXP' }]);
            });
          },
          classes: 'btn-next'
        },
        {
          text: 'Done',
          action: () => {
            tour.complete();
            localStorage.setItem(this.MENUBAR_TOUR_KEY, 'true');
            localStorage.setItem(this.ORDERS_TOUR_KEY, 'true');
          },
          classes: 'btn-next'
        }
      ]
    });

tour.addStep({
  title: 'Search Results',
  text: 'This table displays the list of orders retrieved from your search filters.',
  attachTo: {
    element: 'neo-matris-table',
    on: 'top'
  },
  buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
});

    tour.start();
}

startViewPackagesTour() {
    if (this.activeTour) {
    return;
  }
  const tour = new Shepherd.Tour({
    defaultStepOptions: {
      cancelIcon: { enabled: true },
      scrollTo: { behavior: 'smooth', block: 'center' },
      classes: 'shepherd-theme-arrows'
    },
    useModalOverlay: true
  });

  this.activeTour = tour;
   tour.addStep({
  id: 'intro',
  title: 'Impulses Filters',
  text: 'Use these fields to filter by packages.',
  modalOverlayOpeningPadding: 0,
  classes: 'shepherd-theme-arrows shepherd-centered',
  buttons: [
    {
      text: 'Next',
      action: tour.next,
      classes: 'btn-next'
    }
  ]
});
  const clearActive = () => this.activeTour = null;
  tour.on('cancel', clearActive);
  tour.on('complete', clearActive);
  tour.addStep({
    title: 'Package ID Filter',
    text: 'Enter a package ID.',
    attachTo: {
      element: '#packageID', 
      on: 'bottom'
    },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Description Filter',
    text: 'Search for packages based on their name.',
    attachTo: {
      element: '#packageName', 
      on: 'bottom'
    },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

   tour.addStep({
    title: 'Purchaser Code',
    text: 'Enter a purchaser code.',
    attachTo: {
      element: '#purchaserCode',
      on: 'bottom'
    },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Package Date From',
    text: 'Enter the start date for the package period',
    attachTo: {
      element: '#packageDateFrom',
      on: 'bottom'
    },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Package Date To',
    text: 'Enter the end date for the package period.',
    attachTo: {
      element: '#packageDateTo',
      on: 'bottom'
    },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Prototype Lead Time During Development',
    text: 'Select the prototype lead time from the drop-down menu',
    attachTo: {
      element: '#protLeadTimeDev',
      on: 'bottom'
    },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'SQA',
    text: 'Select the SQA from the drop-down menu',
    attachTo: {
      element: '#sqa',
      on: 'bottom'
    },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Search Button',
    text: 'Click this button to fetch results based on your filters.',
    attachTo: {
      element: '#search', 
      on: 'bottom'
    },
    buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
  title: 'Clear',
  text: 'Click this button to clear the previous search.',
  attachTo: {
    element: '#clear',
    on: 'bottom'
  },
  buttons: [ { text: 'Back', action: tour.back, classes: 'btn-next' },
    {
      text: 'Done',
      action: tour.complete,
      classes: 'btn-next'
    },
    {
      text: 'Explore Create Package',
      action: () => {
        sessionStorage.setItem('continueCreatePackageTour', 'true');
        tour.complete();
        this.zone.run(() => {
          this.router.navigate(['/part-package/create-package'], {
            queryParams: { continueTour: 'true' }
          });
        });
      },
      classes: 'btn-next'
    }
  ]
});

  tour.start();
}

startCreatePackageTour() {
   if (this.activeTour) {
    console.warn('A tour is already running.');
    return;
  }
  const tour = new Shepherd.Tour({
    defaultStepOptions: {
      cancelIcon: { enabled: true },
      scrollTo: { behavior: 'smooth', block: 'center' },
      classes: 'shepherd-theme-arrows'
    },
    useModalOverlay: true
  });
  this.activeTour = tour;
  tour.addStep({
  id: 'intro',
  title: 'Impulses Filters',
  text: 'Use these fields to create a new package.',
  modalOverlayOpeningPadding: 0,
  classes: 'shepherd-theme-arrows shepherd-centered',
  buttons: [
    {
      text: 'Next',
      action: tour.next,
      classes: 'btn-next'
    }
  ]
});
  const clearActive = () => this.activeTour = null;
  tour.on('cancel', clearActive);
  tour.on('complete', clearActive);
  tour.addStep({
    title: 'Package ID',
    text: 'Package ID is created when package details have been entered and saved',
    attachTo: {
      element: '#pckid',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Package Name',
    text: 'Enter a name for the package.',
    attachTo: {
      element: '#packageName',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Package Note',
    text: 'Enter a note or description for the package.',
    attachTo: {
      element: '#packageNote',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Lead Time (RFQ to Nomination)',
    text: 'Select one of the lead time options from the drop-down menu.',
    attachTo: {
      element: '#leadTimeFinalRFQNomination',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Prototype Lead Time',
    text: 'Select one of the prototype lead time options from the drop-down menu.',
    attachTo: {
      element: '#leadTimeDev',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Autocalculate',
    text: 'Click this button to autocalculate the package planning fields based on the selected inputs.',
    attachTo: {
      element: '#autocalc',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Parts Table',
    text: 'Enter the package planning details in the table',
    attachTo: {
      element: 'neo-matris-table',
      on: 'top'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Save Package',
    text: 'Click this button to save the package planning and receive the package ID.',
    attachTo: {
      element: '#save',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

tour.addStep({
  title: 'Close Package',
  text: 'Click this button to close the package. NOTE! Once closed, the package is locked and cannot be edited.',
  attachTo: {
    element: '#close',
    on: 'bottom'
  },
  buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },
    {
      text: 'Done',
      action: tour.complete,
      classes: 'btn-next'
    }

  ]
});


  tour.start();
}

  startFYCTour() {
  const tour = new Shepherd.Tour({
    defaultStepOptions: {
      cancelIcon: { enabled: true },
      scrollTo: { behavior: 'smooth', block: 'center' },
      classes: 'shepherd-theme-arrows',
    },
    useModalOverlay: true
  });


  tour.addStep({
    title: 'Material Group',
    text: 'Enter the material group here to filter results.',
    attachTo: {
      element: '#materialGroup',
      on: 'bottom'
    },
    buttons: [{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });
 tour.addStep({
    title: 'Materialgroup Description',
    text: 'Filter by description',
    attachTo: {
      element: '#materialGroupDescription',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });
  tour.addStep({
    title: 'Purchaser Code',
    text: 'Filter by purchaser code.',
    attachTo: {
      element: '#purchaserCode',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  
tour.addStep({
    title: 'User Name',
    text: 'Filter by Username.',
    attachTo: {
      element: '#username',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Role',
    text: 'Filter by role',
    attachTo: {
      element: '#role',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });
  tour.addStep({
    title: 'Brand',
    text: 'Filter by brand.',
    attachTo: {
      element: '#brand',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Next', action: tour.next, classes: 'btn-next' }]
  });

  tour.addStep({
    title: 'Search',
    text: 'Click to search with the provided filters.',
    attachTo: {
      element: '#search-button',
      on: 'bottom'
    },
    buttons: [{ text: 'Back', action: tour.back, classes: 'btn-next' },{ text: 'Done', action: tour.complete, classes: 'btn-next' }]
  });

  tour.start();
}
startAssignedPartTour(searchCallback?: () => void) {
  console.log('startAssignedPartTour called');
  const alreadyShown =
  localStorage.getItem(
    this.ASSIGNED_PART_TOUR_KEY
  ) === 'true';


  console.log(
  'alreadyShown',
  alreadyShown,
);

  if (alreadyShown) {
    return;
  }

  const tour = new Shepherd.Tour({
    defaultStepOptions: {
      scrollTo: true,
      cancelIcon: { enabled: true }
    }
  });

  tour.on('cancel', () => {
    localStorage.setItem(this.MENUBAR_TOUR_KEY, 'true');
    localStorage.setItem(this.ASSIGNED_PART_TOUR_KEY, 'true');
  });

  tour.addStep({
    id: 'assigned-part-intro',
    title: 'Assigned Parts',
    text: 'Use these filters to search assigned parts.',
    classes: 'shepherd-theme-arrows shepherd-centered',
    buttons: [
      {
        text: 'Next',
        action: tour.next,
        classes: 'btn-next'
      }
    ]
  });

  tour.addStep({
    title: 'Part No',
    text: 'Enter a part number to search assigned parts.',
    attachTo: {
      element: '#partNo',
      on: 'bottom'
    },
    buttons: [
      { text: 'Back', action: tour.back, classes: 'btn-next' },
      { text: 'Next', action: tour.next, classes: 'btn-next' }
    ]
  });

  tour.addStep({
    title: 'Purchaser Code',
    text: 'Search by purchaser code.',
    attachTo: {
      element: '#purchaserCode',
      on: 'bottom'
    },
    buttons: [
      { text: 'Back', action: tour.back, classes: 'btn-next' },
      { text: 'Next', action: tour.next, classes: 'btn-next' }
    ]
  });

  tour.addStep({
    title: 'Sender Name',
    text: 'Filter using sender supplier name.',
    attachTo: {
      element: '#senderName',
      on: 'bottom'
    },
    buttons: [
      { text: 'Back', action: tour.back, classes: 'btn-next' },
      { text: 'Next', action: tour.next, classes: 'btn-next' }
    ]
  });

  tour.addStep({
    title: 'Sender Supplier Number',
    text: 'Filter using sender supplier number.',
    attachTo: {
      element: '#senderSupplier',
      on: 'bottom'
    },
    buttons: [
      { text: 'Back', action: tour.back, classes: 'btn-next' },
      { text: 'Next', action: tour.next, classes: 'btn-next' }
    ]
  });

  tour.addStep({
    title: 'Receiver Name',
    text: 'Filter using receiver supplier name.',
    attachTo: {
      element: '#receiverName',
      on: 'bottom'
    },
    buttons: [
      { text: 'Back', action: tour.back, classes: 'btn-next' },
      { text: 'Next', action: tour.next, classes: 'btn-next' }
    ]
  });

  tour.addStep({
    title: 'Receiver Supplier Number',
    text: 'Filter using receiver supplier number.',
    attachTo: {
      element: '#receiverSupplier',
      on: 'bottom'
    },
    buttons: [
      { text: 'Back', action: tour.back, classes: 'btn-next' },
      { text: 'Next', action: tour.next, classes: 'btn-next' }
    ]
  });

  tour.addStep({
    title: 'State',
    text: 'Filter assigned parts by state.',
    attachTo: {
      element: '#state',
      on: 'bottom'
    },
    buttons: [
      { text: 'Back', action: tour.back, classes: 'btn-next' },
      { text: 'Next', action: tour.next, classes: 'btn-next' }
    ]
  });

  tour.addStep({
    title: 'Search',
    text: 'Click Search to retrieve assigned parts.',
    attachTo: {
      element: '#search',
      on: 'bottom'
    },
    buttons: [
      { text: 'Back', action: tour.back, classes: 'btn-next' },
      {
        text: 'Next',
        action: tour.next,
        classes: 'btn-next'
      }
    ]
  });


  tour.addStep({
    title: 'Clear',
    text: 'Clear all filters and search results.',
    attachTo: {
      element: '#clear',
      on: 'bottom'
    },
    buttons: [
      { text: 'Back', action: tour.back, classes: 'btn-next' },
      { text: 'Next', action: tour.next, classes: 'btn-next' }
    ]
  });

  tour.addStep({
    title: 'Create New Assigned Part',
    text: 'Click here to create a new Assigned Part.',
    attachTo: {
      element: '#create',
      on: 'bottom'
    },
    buttons: [
      { text: 'Back', action: tour.back, classes: 'btn-next' },
      {
        text: 'Done',
        action: () => {
          tour.complete();
          localStorage.setItem(this.MENUBAR_TOUR_KEY, 'true');
          localStorage.setItem(this.ASSIGNED_PART_TOUR_KEY, 'true');
        },
        classes: 'btn-next'
      }
    ]
  });

  tour.start();
}

addIntroStep(): void {
  this.tour.addStep({
    title: 'Need a Tour?',
    text: 'Would you like a quick guide through the features of MATRIS NEO?',
    buttons: [
      {
        text: 'No, skip',
        action: () => {
          this.tour.complete();
          localStorage.setItem(this.MENUBAR_TOUR_KEY, 'true');
        },
        classes: 'btn-skip'
      },
      {
        text: 'Yes, show me',
        action: this.tour.next,
        classes: 'btn-next'
      }
    ]
  });
}

}